import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { PageHeader } from "../../components/admin/ui";
import { useAuth } from "../../context/AuthContext";
import { SiteGateCard } from "../../components/admin/SiteGateCard";
import { SagBillingCard } from "../../components/admin/SagBillingCard";
import { ZoomCard } from "../../components/admin/ZoomCard";
import { SecretInput } from "../../components/admin/SecretInput";
import { GatewayTestTxn } from "../../components/admin/GatewayTestTxn";
import { OutboundIp } from "../../components/admin/OutboundIp";
import { GatewayLiveTest } from "../../components/admin/GatewayLiveTest";
import { OperatingModeCard } from "../../components/admin/OperatingModeCard";

const BADGE = { not_configured: "bg-slate-200 text-slate-800", untested: "bg-amber-100 text-amber-900", passed: "bg-green-100 text-green-800", failed: "bg-red-100 text-red-800" };
const StatusBadge = ({ status, testid }) => <span className={`rounded px-2 py-1 text-xs font-bold ${BADGE[status.state]}`} data-testid={testid}>{status.label}</span>;

const GATEWAY = [["merchant_username", "Merchant username"], ["mode", "Mode", ["test", "live"]], ["currency", "Currency (ISO, e.g. usd)"],
  ["capture", "Capture", ["sale", "auth_only"]], ["endpoint_url", "Gateway endpoint URL (approved HTTPS endpoint)"]];
const BOGUS = /^[\s•●·∙*◦]*$/;
const bogus = (s) => BOGUS.test(s) || ["blocked", "saved", "not set"].includes(s.trim().toLowerCase());

function clientErrors(kind, v, secret, data) {
  if (kind !== "gateway") return {};
  const e = {};
  if (v.endpoint_url.trim() && !data.approved_endpoints.includes(v.endpoint_url.trim())) e.endpoint_url = `Use the approved gateway endpoint exactly: ${data.approved_endpoints[0]}`;
  if (secret && bogus(secret)) e.merchant_password = "The password contains only dots, spaces or \"Blocked\" (often inserted by a browser security extension). Type the real password.";
  if (v.merchant_username && bogus(v.merchant_username)) e.merchant_username = "Enter the real merchant username.";
  if (!/^[A-Za-z]{3}$/.test(v.currency.trim())) e.currency = "Use a 3-letter currency code, e.g. usd.";
  return e;
}

const FieldError = ({ id, msg }) => (msg ? <p id={id} className="mt-1 text-xs font-semibold text-red-700" role="alert" data-testid={id}>{msg}</p> : null);
const Override = ({ on, testid }) => (on ? <p className="mt-1 text-xs text-amber-800" data-testid={testid}>Overridden by server setting{testid === "gateway-mode-override" ? " (affects only the connection test; the Operating mode decides public checkout)" : ""}</p> : null);
const SMTP = [["host", "SMTP host"], ["port", "Port"], ["security", "Encryption", ["ssl", "starttls", "none"]], ["username", "Username"], ["from_address", "Sender address"]];

function Section({ kind, title, fields, secretLabel, data, onSaved, children }) {
  const init = () => Object.fromEntries(fields.map(([f]) => [f, data.fields[f].value]));
  const [v, setV] = useState(init);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [errs, setErrs] = useState({});
  const sk = data.secret.name;
  useEffect(() => { setV(init()); setSecret(""); setErrs({}); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (e) => {
    e.preventDefault();
    const ce = clientErrors(kind, v, secret, data);
    setErrs(ce);
    if (Object.keys(ce).length) { toast.error("Nothing was saved. Fix the highlighted fields."); return; }
    setBusy(true);
    const body = { ...v, ...(kind === "smtp" ? { port: Number(v.port) } : {}), [data.secret.name]: secret || null };
    try {
      const { data: r } = await api.put(`/admin/integrations/${kind}`, body);
      const pw = r.secret_updated ? "Password updated" : r.secret_cleared ? "Password cleared" : "Password kept (unchanged)";
      toast.success(r.reset ? `Saved. ${pw}.` : `No changes to save. ${pw}; status and activation kept.`);
      setNotice(r.reset && kind === "smtp" ? "Settings changed: run Test SMTP connection and switch Use SMTP back on." : r.reset ? "Settings changed: run the connection test and switch it back on." : "");
      onSaved();
    }
    catch (err) {
      const fe = err?.response?.data?.detail?.fields;
      if (fe) { setErrs(fe); toast.error("Nothing was saved. Fix the highlighted fields."); return; } // keep the typed password for a retry
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };
  const sending = kind === "smtp" && data.use_for_email && data.status.state === "passed";
  return (
    <section className="admin-card space-y-4" data-testid={`integration-${kind}`}>
      <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="text-lg text-navy">{title}</h2>
        <div className="flex flex-wrap items-center gap-2">
          {kind === "smtp" && <span className={`rounded px-2 py-1 text-xs font-bold ${sending ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-800"}`} data-testid="smtp-sending-badge">
            Sending emails: {sending ? "ON" : "OFF (log only)"}</span>}
          <StatusBadge status={data.status} testid={`${kind}-status-badge`} /></div></div>
      {notice && <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm font-semibold text-amber-900" role="status" data-testid={`${kind}-reset-notice`}>{notice}</p>}
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2" autoComplete="off" noValidate>
        {fields.map(([f, label, opts]) => (
          <div key={f}><label htmlFor={`${kind}-${f}`} className="field-label">{label}</label>
            {opts ? <select id={`${kind}-${f}`} className="field-input" value={v[f]} onChange={(e) => setV({ ...v, [f]: e.target.value })} data-testid={`${kind}-${f}-input`}>{opts.map((o) => <option key={o}>{o}</option>)}</select>
              : <input id={`${kind}-${f}`} type={f === "endpoint_url" ? "url" : "text"} className="field-input" value={v[f]} aria-invalid={!!errs[f]} aria-describedby={errs[f] ? `${kind}-${f}-error` : undefined}
                  onChange={(e) => setV({ ...v, [f]: e.target.value })} data-testid={`${kind}-${f}-input`} />}
            <Override on={data.fields[f].source === "env"} testid={`${kind}-${f}-override`} />
            <FieldError id={`${kind}-${f}-error`} msg={errs[f]} />
          </div>))}
        <div><label htmlFor={`${kind}-secret`} className="field-label">{secretLabel}</label>
          {kind === "gateway"
            ? <input id={`${kind}-secret`} type="password" autoComplete="new-password" className="field-input" value={secret} onChange={(e) => setSecret(e.target.value)} aria-invalid={!!errs[sk]}
                aria-describedby={errs[sk] ? `${kind}-secret-error` : `${kind}-secret-hint`} data-testid={`${kind}-secret-input`} />
            : <SecretInput id={`${kind}-secret`} value={secret} onChange={(e) => setSecret(e.target.value)}
                placeholder={data.secret.set ? "•••••••• saved (leave blank to keep)" : "not set"} data-testid={`${kind}-secret-input`} />}
          {kind === "gateway" && <p id={`${kind}-secret-hint`} className="mt-1 text-xs text-slate-600" data-testid={`${kind}-secret-state`}>{data.secret.set ? "Saved. Leave blank to keep it." : "Not set."}</p>}
          <Override on={data.secret.source === "env"} testid={`${kind}-secret-override`} />
          <FieldError id={`${kind}-secret-error`} msg={errs[sk]} />
        </div>
        <div className="flex items-end"><button className="btn-navy" disabled={busy} data-testid={`${kind}-save-btn`}>{busy ? "Saving…" : "Save settings"}</button></div>
      </form>
      {children}
    </section>
  );
}

function Actions({ kind, data, toggleField, toggleLabel, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState("");
  const run = async (path, body) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/integrations/${kind}/${path}`, body);
      const failed = r.data.ok === false || r.data.status?.state === "failed";
      (failed ? toast.error : toast.success)(r.data.message || r.data.status?.label || "Done", failed ? { duration: 15000 } : undefined);
      onChanged();
    }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const canEnable = data.status.state === "passed";
  const why = data.status.state === "not_configured" ? "Disabled: fill in and save the settings first."
    : data.status.state === "failed" ? "Disabled: the last connection test failed. Fix the settings and run the test again."
      : "Disabled: run the connection test above first. It must pass, then this box unlocks immediately.";
  return (
    <div className="space-y-3 border-t pt-4">
      <button type="button" className="btn-outline" disabled={busy || data.status.state === "not_configured"} onClick={() => run("test")} data-testid={`${kind}-test-btn`}>
        {busy ? "Testing…" : kind === "gateway" ? "Test gateway connection (no charge)" : "Test SMTP connection (login only)"}</button>
      {data.status.detail && <p className="text-xs text-slate-600">{data.status.detail}</p>}
      {kind === "smtp" && data.send_test?.ok && <p><span className="rounded bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-900" data-testid="smtp-send-test-badge">Test email accepted by server {data.send_test.at.slice(0, 16).replace("T", " ")} UTC</span></p>}
      {kind === "smtp" && (
        <div className="flex flex-wrap items-end gap-2">
          <div><label htmlFor="smtp-test-to" className="field-label">Send a test email to</label><input id="smtp-test-to" type="email" className="field-input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="smtp-test-to-input" /></div>
          <button type="button" className="btn-outline" disabled={busy || !to || data.status.state === "not_configured"} onClick={() => run("send-test", { to })} data-testid="smtp-send-test-btn">Send test email</button>
        </div>)}
      {toggleField !== "use_for_payments" && <label className={`flex items-center gap-2 text-sm font-semibold ${canEnable ? "" : "opacity-60"}`}>
        <input type="checkbox" checked={data[toggleField]} disabled={busy || (!canEnable && !data[toggleField])} onChange={(e) => run("use", { enabled: e.target.checked })} data-testid={`${kind}-use-toggle`} />
        {toggleLabel}
      </label>}
      {toggleField === "use_for_payments" && <p className="text-xs text-slate-600" data-testid="gateway-mode-note">Which checkout the public uses is set by the Operating mode above (Demo = simulated, Live = this gateway).</p>}
      {toggleField !== "use_for_payments" && !canEnable && !data[toggleField] && <p className="text-xs font-semibold text-amber-800" data-testid={`${kind}-use-disabled-reason`}>{why}</p>}
    </div>
  );
}

export default function Integrations() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/integrations").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (user?.role === "super_admin") load(); }, [user]);
  if (user?.role !== "super_admin") return <p className="admin-card font-semibold text-amber-900" data-testid="integrations-forbidden">Integrations are available to Super Admins only. Only a Super Admin can switch on email sending.</p>;
  if (!d) return null;
  return (
    <div className="space-y-6" data-testid="admin-integrations">
      <PageHeader title="Integrations" subtitle="Super Admin only. Passwords are write-only and stored encrypted; a value set in the server configuration overrides the saved one." />
      <div className="admin-card text-sm" data-testid="integrations-active">Payments now: <b>{d.active.payments}</b> · Email now: <b>{d.active.email}</b></div>
      <OperatingModeCard />
      <Section kind="gateway" title="Card payment gateway" fields={GATEWAY} secretLabel="Remote API password" data={d.gateway} onSaved={load}>
        <Actions kind="gateway" data={d.gateway} toggleField="use_for_payments" toggleLabel="Use the card payment gateway for payments" onChanged={load} />
        <GatewayTestTxn diag={d.gateway.diag} onChanged={load} />
        <OutboundIp />
        <GatewayLiveTest />
      </Section>
      <Section kind="smtp" title="SMTP (outgoing email)" fields={SMTP} secretLabel="SMTP password" data={d.smtp} onSaved={load}>
        <Actions kind="smtp" data={d.smtp} toggleField="use_for_email" toggleLabel="Use SMTP to send emails" onChanged={load} />
      </Section>
      <ZoomCard data={d.zoom} onChanged={load} />
      <SiteGateCard />
      <SagBillingCard />
      <section className="admin-card"><h2 className="text-lg text-navy">Change log</h2>
        <ul className="mt-2 text-xs" data-testid="integrations-audit">{d.audit.map((a, i) => <li key={i} className="border-b py-1">{a.at.slice(0, 16).replace("T", " ")} · {a.kind} · {a.action} · {a.by}{a.fields.length ? ` · fields: ${a.fields.join(", ")}` : ""}</li>)}</ul>
      </section>
    </div>
  );
}
